import React from 'react';
import { ArrowDown, Github, Linkedin, Twitter, Instagram, Bot, BookOpen } from 'lucide-react';
import { Button } from './ui/button';
import { personalInfo } from '../data/mock';

const HeroSection = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="home"
      className="min-h-screen flex items-center justify-center relative overflow-hidden bg-zinc-950"
    >
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,#09090b_70%)]" />
      </div>

      {/* Grid Pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}
      />

      <div className="max-w-6xl mx-auto px-6 py-20 relative z-10">
        <div className="flex flex-col items-center text-center">
          {/* Status Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-900/80 border border-zinc-800 rounded-full mb-8">
            <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
            <span className="text-sm text-zinc-400">Available for opportunities</span>
          </div>

          {/* Name */}
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-4 tracking-tight">
            {personalInfo.name}
          </h1>

          {/* Title */}
          <h2 className="text-xl md:text-2xl text-cyan-400 font-medium mb-4">
            {personalInfo.title}
          </h2>

          {/* Subtitle */}
          <p className="text-zinc-400 text-lg md:text-xl max-w-2xl mb-8">
            {personalInfo.subtitle}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <Button
              size="lg"
              className="bg-cyan-500 hover:bg-cyan-600 text-zinc-950 font-medium px-8"
              onClick={() => scrollToSection('#contact')}
            >
              Contact Me
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white px-8"
              onClick={() => scrollToSection('#projects')}
            >
              View Projects
            </Button>
          </div>

          {/* AI Assistant Card */}
          <a
            href={personalInfo.aiAssistant.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group mb-10 p-4 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-2xl hover:border-cyan-400/50 transition-all duration-300 hover:-translate-y-1 max-w-md w-full"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-cyan-500/20 rounded-xl flex items-center justify-center group-hover:bg-cyan-500/30 transition-colors">
                <Bot className="w-7 h-7 text-cyan-400" />
              </div>
              <div className="text-left flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold text-white">{personalInfo.aiAssistant.name}</span>
                  <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full">AI</span>
                </div>
                <p className="text-zinc-400 text-sm">{personalInfo.aiAssistant.description}</p>
              </div>
              <div className="text-zinc-500 group-hover:text-cyan-400 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
            <p className="text-zinc-500 text-xs mt-3 text-center">Chat with my AI assistant to learn more about me</p>
          </a>

          {/* Social Links */}
          <div className="flex items-center gap-4">
            <a
              href={personalInfo.socialLinks.github}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 bg-zinc-900/50 border border-zinc-800 rounded-lg text-zinc-400 hover:text-white hover:border-zinc-700 transition-all hover:-translate-y-1"
              title="GitHub"
            >
              <Github size={20} />
            </a>
            <a
              href={personalInfo.socialLinks.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 bg-zinc-900/50 border border-zinc-800 rounded-lg text-zinc-400 hover:text-white hover:border-zinc-700 transition-all hover:-translate-y-1"
              title="LinkedIn"
            >
              <Linkedin size={20} />
            </a>
            <a
              href={personalInfo.socialLinks.twitter}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 bg-zinc-900/50 border border-zinc-800 rounded-lg text-zinc-400 hover:text-white hover:border-zinc-700 transition-all hover:-translate-y-1"
              title="Twitter"
            >
              <Twitter size={20} />
            </a>
            <a
              href={personalInfo.socialLinks.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 bg-zinc-900/50 border border-zinc-800 rounded-lg text-zinc-400 hover:text-white hover:border-zinc-700 transition-all hover:-translate-y-1"
              title="Instagram"
            >
              <Instagram size={20} />
            </a>
            <a
              href={personalInfo.socialLinks.medium}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 bg-zinc-900/50 border border-zinc-800 rounded-lg text-zinc-400 hover:text-white hover:border-zinc-700 transition-all hover:-translate-y-1"
              title="Medium"
            >
              <BookOpen size={20} />
            </a>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <button
            onClick={() => scrollToSection('#about')}
            className="p-2 text-zinc-500 hover:text-white transition-colors"
          >
            <ArrowDown size={24} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
